import { Fragment, useState } from "react";

import { ChevronRight } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { asRecordArray } from "@/lib/tina/value-readers";
import { cn } from "@/lib/utils";

/**
 * Renders Tina's deterministic score breakdown — the per-stage confidence
 * delta table plus penalties, caps and issues.
 *
 * Shared between the HITL low_score panel and the Done panel so the
 * confidence explanation is available for every item that ran the score
 * stage, not just items that landed in HITL (MOO-4455).
 */
export function ScoreBreakdownView({
  breakdown,
}: {
  breakdown: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);
  const stageRows = asRecordArray(breakdown.stage_deltas);
  const penalties = asRecordArray(breakdown.penalties);
  const caps = asRecordArray(breakdown.caps_applied);
  const issues = asStringList(breakdown.issues);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="flex flex-col gap-3 rounded-md border bg-muted/20 p-3"
    >
      <CollapsibleTrigger className="group inline-flex items-center gap-1 self-start text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground">
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            open && "rotate-90",
          )}
        />
        Score breakdown
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-3">
        {stageRows.length > 0 && (
          <div className="overflow-x-auto rounded-md border bg-background">
            <table className="w-full min-w-[420px] text-left text-xs">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Stage</th>
                  <th className="px-2 py-1.5 font-medium">Delta</th>
                  <th className="px-2 py-1.5 font-medium">Running</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {stageRows.map((row, index) => {
                  const delta =
                    typeof row.confidence_delta === "number"
                      ? row.confidence_delta
                      : null;
                  const isReduction = delta !== null && delta < 0;
                  const reasons = asRecordArray(row.reasons);
                  return (
                    <Fragment key={`${String(row.stage ?? "stage")}-${index}`}>
                      <tr>
                        <td className="px-2 py-1.5 font-medium">
                          {formatUnknownDisplay(row.stage)}
                        </td>
                        <td
                          className={
                            isReduction
                              ? "px-2 py-1.5 font-mono tabular-nums text-rose-600 dark:text-rose-400"
                              : "px-2 py-1.5 font-mono tabular-nums"
                          }
                        >
                          {formatSignedScoreValue(row.confidence_delta)}
                        </td>
                        <td className="px-2 py-1.5 font-mono tabular-nums">
                          {formatScoreValue(row.running_score)}
                        </td>
                      </tr>
                      {reasons.length > 0 && (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-2 pb-1.5 text-[11px] text-muted-foreground"
                          >
                            <ul className="list-disc pl-4">
                              {reasons.map((reason, i) => (
                                <li key={`${String(row.stage)}-reason-${i}`}>
                                  {formatUnknownDisplay(reason.reason)} (
                                  {formatSignedScoreValue(reason.value)})
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {penalties.length > 0 && (
          <CompactList
            label="Penalties"
            items={penalties.map(formatScoreAdjustment)}
          />
        )}
        {caps.length > 0 && (
          <CompactList label="Caps" items={caps.map(formatScoreCap)} />
        )}
        {issues.length > 0 && <CompactList label="Issues" items={issues} />}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** True when a stage-output artifact's content looks like a score breakdown. */
export function isScoreBreakdown(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "final_score" in value &&
    "passed_threshold" in value
  );
}

function CompactList({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <ul className="list-disc space-y-1 pl-4">
        {items.map((item, index) => (
          <li key={`${label}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function formatScoreAdjustment(row: Record<string, unknown>) {
  const rule = formatUnknownDisplay(row.rule);
  const value = formatSignedScoreValue(row.value);
  const reason =
    typeof row.reason === "string" && row.reason.trim()
      ? row.reason.trim()
      : null;
  const detail =
    reason ??
    formatUnknownDisplay(row.classification ?? row.count ?? row.total);
  return detail === "—" ? `${rule}: ${value}` : `${rule}: ${value} · ${detail}`;
}

function formatScoreCap(row: Record<string, unknown>) {
  const rule = formatUnknownDisplay(row.rule);
  const cap = formatScoreValue(row.cap);
  const reason = formatUnknownDisplay(row.reason);
  return reason === "—" ? `${rule}: ${cap}` : `${rule}: ${cap} · ${reason}`;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map(formatUnknownDisplay).filter((entry) => entry !== "—")
    : [];
}

function formatUnknownDisplay(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || "—";
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  return "—";
}

function formatScoreValue(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toFixed(2);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : value;
  }
  return "—";
}

function formatSignedScoreValue(value: unknown): string {
  const formatted = formatScoreValue(value);
  if (formatted === "—" || formatted.startsWith("-")) return formatted;
  const parsed = Number(formatted);
  return Number.isFinite(parsed) && parsed > 0 ? `+${formatted}` : formatted;
}
